'use client';

import * as React from 'react';
import { ComponentProps, ReactNode } from 'react';

import { PrefetchLink } from '@/ui';
import { cn } from '@lib';
import { type VariantProps, cva } from 'class-variance-authority';

const buttonVariants = cva(
  "focus-visible:border-ring focus-visible:ring-input/80 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive relative inline-flex cursor-pointer items-center justify-center gap-2 overflow-hidden rounded-md text-sm font-medium whitespace-nowrap transition-colors outline-none focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: [
          'bg-primary text-primary-foreground hover:text-accent-foreground shadow-xs',
          'before:bg-accent before:absolute before:inset-0 before:origin-left before:scale-x-0 before:transition-transform hover:before:scale-x-100',
          'isolate before:-z-10',
        ].join(' '),
        destructive: [
          'bg-destructive text-destructive-foreground shadow-xs',
          'before:bg-destructive/90 before:absolute before:inset-0 before:origin-left before:scale-x-0 before:transition-transform hover:before:scale-x-100',
          'isolate before:-z-10',
        ].join(' '),
        outline: [
          'border-input hover:text-accent-foreground hover:border-accent border bg-transparent shadow-xs',
          'before:bg-accent before:absolute before:inset-0 before:origin-left before:scale-x-0 before:transition-transform hover:before:scale-x-100',
          'isolate before:-z-10',
        ].join(' '),
        secondary: [
          'bg-secondary text-secondary-foreground shadow-xs',
          'before:bg-secondary/80 before:absolute before:inset-0 before:origin-left before:scale-x-0 before:transition-transform hover:before:scale-x-100',
          'isolate before:-z-10',
        ].join(' '),
        ghost: [
          'hover:text-accent-foreground hover:shadow-xs',
          'before:bg-accent before:absolute before:inset-0 before:origin-left before:scale-x-0 before:transition-transform hover:before:scale-x-100',
          'isolate before:-z-10',
        ].join(' '),
        link: 'text-primary underline-offset-4 hover:underline',
      },
      disabled: {
        true: 'pointer-events-none',
      },
      size: {
        default: 'h-10 px-4 py-2 sm:text-base',
        responsive: 'h-9 px-3 text-sm sm:h-10 sm:px-4 sm:text-base',
        sm: 'h-8 rounded-md px-3 text-sm sm:h-9',
        lg: 'h-12 rounded-md px-5 sm:text-base',
        icon: 'h-10 w-10',
        smIcon: 'size-8 sm:size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
    compoundVariants: [
      {
        variant: ['default', 'destructive', 'secondary'],
        disabled: true,
        className: 'bg-muted text-muted-foreground before:hidden',
      },
      {
        variant: ['link', 'ghost', 'outline'],
        disabled: true,
        className: 'opacity-50',
      },
    ],
  },
);

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> &
  Omit<ComponentProps<typeof PrefetchLink>, 'href'> & {
    /** An href can be assigned to this button to turn it into a link */
    href?: string;
    children?: ReactNode;
    className?: string;
    ref?: React.RefObject<HTMLButtonElement> | React.RefObject<HTMLAnchorElement>;
  };

function Button({ className, variant = 'default', size, href, children, disabled, ref, ...props }: ButtonProps) {
  if (href) {
    return (
      <PrefetchLink
        className={cn(buttonVariants({ variant, size, disabled }), className)}
        href={href}
        data-variant={variant}
        data-component="button"
        ref={ref}
        {...(props as Omit<ComponentProps<typeof PrefetchLink>, 'href'>)}
      >
        {children}
      </PrefetchLink>
    );
  } else {
    return (
      <button
        data-component="button"
        data-variant={variant}
        className={cn(buttonVariants({ variant, size, disabled }), className)}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref={ref as any}
        {...props}
      >
        {children}
      </button>
    );
  }
}

export { Button, buttonVariants };
